import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@ui';
import { MemberAvatar } from '@entities/group';
import { CheckboxTick, Lightbulb, X } from '@ui/icons';
import { cn, formatDecimalWithCommas } from '@shared/lib';
import type { SplitMember } from './SplitModal';

export interface MemberPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemName: string;
  itemPrice: number;
  currencySymbol: string;
  members: SplitMember[];
  /** Currently selected member IDs. */
  selectedIds: string[];
  onAssign: (memberIds: string[]) => void;
}

/**
 * Picker that lets the user choose which members pay for a specific line item.
 * Multiple selections split the item equally among chosen members.
 */
export function MemberPickerModal({
  open,
  onOpenChange,
  itemName,
  itemPrice,
  currencySymbol,
  members,
  selectedIds,
  onAssign,
}: MemberPickerModalProps) {
  const [localSelected, setLocalSelected] = useState<string[]>(selectedIds);

  // Reset local state whenever modal opens with fresh selectedIds
  const handleOpenChange = (next: boolean) => {
    if (next) setLocalSelected(selectedIds);
    onOpenChange(next);
  };

  const toggle = (id: string) => {
    setLocalSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const estimatedShare = localSelected.length > 0 ? itemPrice / localSelected.length : 0;
  // Every member selected ⇒ the link CLEARS all; otherwise it SELECTS all.
  const allSelected = members.length > 0 && localSelected.length === members.length;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-80 bg-black/60" />
        <Dialog.Content
          aria-describedby="member-picker-desc"
          className="fixed left-1/2 top-1/2 z-80 flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-125 -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-surface-raised shadow-overlay focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-4 bg-header-bar px-5 py-3">
            <Dialog.Title className="text-xl font-bold text-[#191C1E] truncate">
              {itemName}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                id="member-picker-close-btn"
                type="button"
                aria-label="Close"
                className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft hover:bg-border focus-visible:outline-none"
              >
                <X className="size-4 text-fg" />
              </button>
            </Dialog.Close>
          </div>

          {/* Sub-title */}
          <p id="member-picker-desc" className="px-6 py-3 text-sm text-[#414755]">
            Item amount {currencySymbol}
            {formatDecimalWithCommas(itemPrice.toFixed(2))}. Pick who pays.
          </p>

          {/* Tip banner */}
          <div className="mx-6 mb-2 flex items-start gap-2.5 rounded-lg bg-[#F2F2F2] px-4 py-2.5 text-xs text-[#191C1E] leading-relaxed">
            <Lightbulb className="size-4.5 shrink-0 text-[#E5A800] mt-0.5" />
            <span>
              <strong className="font-semibold text-[#191C1E]">Tip:</strong> Select more than one
              people to split this item equally.
            </span>
          </div>

          {/* Clear all / Select all — toggles by whether every member is already selected. */}
          <div className="px-6">
            <button
              type="button"
              onClick={() =>
                setLocalSelected(allSelected ? [] : members.map((member) => member.id))
              }
              className="text-sm font-semibold text-[#191C1E] underline hover:opacity-75 cursor-pointer"
            >
              {allSelected ? 'Clear All' : 'Select All'}
            </button>
          </div>

          {/* Members list */}
          <ul className="flex-1 overflow-y-auto px-6 py-2 flex flex-col gap-3">
            {members.map((member) => {
              const isSelected = localSelected.includes(member.id);
              return (
                <li key={member.id}>
                  <button
                    type="button"
                    onClick={() => toggle(member.id)}
                    className={cn(
                      'w-full flex items-center gap-4 rounded-lg border px-4 py-3.5 text-left transition-colors cursor-pointer',
                      isSelected
                        ? 'border-[#4C4ACA] bg-black/2'
                        : 'border-[#E0E0E0] bg-surface-raised hover:bg-canvas/10',
                    )}
                  >
                    {/* Checkbox */}
                    <span
                      className={cn(
                        'flex size-6 shrink-0 items-center justify-center rounded-md border transition-all',
                        isSelected
                          ? 'bg-[#7B79E0] border-[#7B79E0]'
                          : 'bg-[#ECEFF1] border-[#CFD8DC]',
                      )}
                    >
                      {isSelected ? <CheckboxTick className="size-3 text-white" /> : null}
                    </span>

                    {/* Avatar */}
                    <MemberAvatar
                      name={member.name === member.phone ? '' : member.name}
                      phone={member.phone}
                      avatarUrl={member.avatarUrl}
                      size="md"
                      className="shrink-0"
                    />

                    {/* Name */}
                    <span className="flex-1 truncate text-sm font-bold text-[#191C1E]">
                      {member.name}
                    </span>

                    {/* Estimated share */}
                    {isSelected && (
                      <span className="shrink-0 text-right">
                        <span className="block text-sm font-bold text-[#191C1E]">
                          {currencySymbol}
                          {formatDecimalWithCommas(estimatedShare.toFixed(2))}
                        </span>
                        <span className="block text-xs text-[#7D8492] font-medium">Estimated</span>
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Footer */}
          <div className="px-6 py-3 mt-2 border-t border-border-soft">
            <Button
              id="member-picker-assign-btn"
              variant="primary"
              size="md"
              className="bg-[#2A4893] hover:bg-[#1F3770] font-semibold text-white px-6 py-2.5 rounded-md"
              onClick={() => {
                onAssign(localSelected);
                onOpenChange(false);
              }}
            >
              Assign
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
